import { useEffect, useRef } from 'react';
import { categories } from '../../../mocks/categories';

export default function HeroSection() {
  const orbs = useRef<HTMLDivElement[]>([]);

  const scrollToCategories = () => {
    document.getElementById('kategoriler')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    let frame: number;
    let t = 0;
    const animate = () => {
      t += 0.003;
      orbs.current.forEach((el, i) => {
        if (!el) return;
        const x = Math.sin(t + i * 1.2) * 60;
        const y = Math.cos(t * 0.7 + i * 0.9) * 40;
        el.style.transform = `translate(${x}px, ${y}px)`;
      });
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  const floatingIcons = categories.slice(0, 6);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#0a0a0f]">
      {/* Animated gradient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          ref={(el) => { if (el) orbs.current[0] = el; }}
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
        <div
          ref={(el) => { if (el) orbs.current[1] = el; }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
        <div
          ref={(el) => { if (el) orbs.current[2] = el; }}
          className="absolute top-1/2 right-1/3 w-64 h-64 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #ff6b9d 0%, transparent 70%)', filter: 'blur(50px)' }}
        />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(168,85,247,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(168,85,247,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Floating category icons */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {floatingIcons.map((cat, i) => {
          const positions = [
            'top-24 left-[8%]', 'top-32 right-[10%]',
            'top-1/2 left-[4%]', 'top-1/2 right-[6%]',
            'bottom-32 left-[12%]', 'bottom-24 right-[8%]',
          ];
          return (
            <div
              key={cat.id}
              className={`absolute ${positions[i]} hidden lg:flex flex-col items-center gap-1 opacity-30`}
              style={{ animation: `floatIcon ${3.5 + i * 0.4}s ease-in-out ${i * 0.5}s infinite alternate` }}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${cat.gradientFrom}33, ${cat.gradientTo}33)`,
                  border: `1px solid ${cat.gradientFrom}44`,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <i className={`${cat.icon} text-xl`} style={{ color: cat.gradientFrom }} />
              </div>
              <span className="text-[10px] text-white/50 font-medium">{cat.name}</span>
            </div>
          );
        })}
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center px-4 md:px-8 max-w-4xl mx-auto w-full">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-1.5 mb-8">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-purple-300 text-xs font-medium tracking-wide uppercase">
            Türkiye Geneli Aktif Kampanyalar
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          İlgilendiğin Alanı{' '}
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(135deg, #a855f7, #ec4899, #06b6d4)' }}
          >
            Seç
          </span>
          , En İyi Fırsatları{' '}
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(135deg, #f59e0b, #ef4444)' }}
          >
            İlk Sen Öğren
          </span>
        </h1>

        {/* Subtext */}
        <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          Türkiye genelinde yüzlerce sektörde{' '}
          <strong className="text-white">özel kampanyalar</strong> seni bekliyor.
          Bir tıkla WhatsApp'ına gelsin.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
          <button
            onClick={scrollToCategories}
            className="group relative px-8 py-4 rounded-full text-white font-semibold text-lg transition-all duration-300 cursor-pointer whitespace-nowrap overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #a855f7, #6366f1)',
              boxShadow: '0 0 40px rgba(168, 85, 247, 0.5)',
            }}
          >
            <span className="relative z-10 flex items-center gap-2">
              <i className="ri-arrow-right-circle-line" />
              Hemen Alan Seç
            </span>
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
          <button
            onClick={() => document.getElementById('nasil-calisir')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 rounded-full text-gray-300 font-medium text-lg border border-white/15 hover:border-white/30 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Nasıl Çalışır?
          </button>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
          {[
            { label: 'Aktif Kampanya', value: '2.400+' },
            { label: 'Sektör', value: '8+' },
            { label: 'Mutlu Kullanıcı', value: '58.000+' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #a855f7, #06b6d4)', fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {stat.value}
              </div>
              <div className="text-gray-500 text-sm mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50 animate-bounce">
        <span className="text-gray-500 text-xs tracking-wider uppercase">Keşfet</span>
        <i className="ri-arrow-down-line text-gray-500 text-lg" />
      </div>
    </section>
  );
}
